import { QueryClient, type DefaultOptions } from '@tanstack/react-query';
import { isApiError } from '@mailserver/web-shared/api';

/**
 * Retry a failed query once — but not one the server refused with a 4xx, which a retry only repeats.
 *
 * That matters most for `403 no_mailbox` (context BUG-0062): the explanation is shown only once the
 * account query has *failed*, so retrying it held the whole mail view — the folder sidebar's alert, the
 * message list's error, New message — on screen for the retry delay first. `408` and `429` are the two
 * 4xx answers that do mean "try again". A network error is not an `ApiError`, so it is still retried,
 * which is also what lets the service worker answer from the offline cache.
 */
export function retryQuery(failureCount: number, error: unknown): boolean {
  if (isApiError(error) && error.status >= 400 && error.status < 500) {
    return error.status === 408 || error.status === 429 ? failureCount < 1 : false;
  }
  return failureCount < 1;
}

export const webmailQueryDefaults: DefaultOptions = {
  // `offlineFirst`, not the default `online`: when the browser is offline React Query would not issue
  // the request at all, and the service worker never gets the chance to answer it from the offline
  // cache. Attempt it, let the worker serve what it has, and pause retries after that.
  queries: { retry: retryQuery, staleTime: 10_000, refetchOnWindowFocus: false, networkMode: 'offlineFirst' },
  // Same for writes, but for the opposite reason: the default queues a paused mutation and replays it
  // when the connection returns, which for a mail client means a message sending itself later. Fail
  // now and show the error instead.
  mutations: { networkMode: 'offlineFirst' },
};

export const createQueryClient = () => new QueryClient({ defaultOptions: webmailQueryDefaults });
